import type { IDivisionResponse, IDivisionsPayload } from '../../myTeam/types/myTeam';

const normalizeDivisionKey = (division: IDivisionResponse): string =>
  division.divisionName?.trim().toLowerCase() || division.divisionId?.trim() || '';

const getDivisionTeamsArray = (division: IDivisionResponse): unknown[] => {
  if (!division) return [];

  const teams = division.teams;
  if (Array.isArray(teams)) return teams;

  const alt = (division as unknown as Record<string, unknown>).teamSnapshotResponseList;
  return Array.isArray(alt) ? alt : [];
};

const getDivisionRank = (division: IDivisionResponse): number => {
  let rank = 0;

  if ((division.childDivisions ?? []).length > 0) rank += 2;
  if (getDivisionTeamsArray(division).length > 0) rank += 1;

  return rank;
};

export const extractDivisionList = (
  payload: IDivisionsPayload | IDivisionResponse[] | undefined
): IDivisionResponse[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const maybeResponses = payload as IDivisionsPayload & {
    divisionResponses?: IDivisionResponse[];
  };

  return maybeResponses.divisionResponseList ?? maybeResponses.divisionResponses ?? [];
};

export const dedupeDivisionResponses = (
  divisions: IDivisionResponse[]
): IDivisionResponse[] => {
  const deduped: IDivisionResponse[] = [];
  const seenIds = new Set<string>();

  divisions.forEach((division) => {
    const divisionId = division.divisionId?.trim();
    if (divisionId && seenIds.has(divisionId)) return;
    if (divisionId) seenIds.add(divisionId);

    const key = normalizeDivisionKey(division);
    if (!key) {
      deduped.push(division);
      return;
    }

    const existingIndex = deduped.findIndex(
      (item) => normalizeDivisionKey(item) === key
    );

    if (existingIndex === -1) {
      deduped.push(division);
      return;
    }

    if (getDivisionRank(division) > getDivisionRank(deduped[existingIndex])) {
      deduped[existingIndex] = division;
    }
  });

  return deduped;
};

export const extractConfiguredDivisionList = (
  payload: IDivisionsPayload | IDivisionResponse[] | undefined
): IDivisionResponse[] => dedupeDivisionResponses(extractDivisionList(payload));
